import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Card, Row, Col, Button, Space, Table, Tag, Typography, Breadcrumb,
  Empty, Modal, Form, Input, Select, Upload, message, Spin, Tooltip,
  Divider, Statistic, Badge, Tree, List, Radio
} from 'antd';
import {
  FolderOutlined, FileOutlined, UploadOutlined, DeleteOutlined,
  DownloadOutlined, FolderAddOutlined, SearchOutlined,
  ShareAltOutlined, TeamOutlined, FilePdfOutlined, FileImageOutlined,
  FileWordOutlined, FileExcelOutlined, FileTextOutlined,
  HomeOutlined, CopyOutlined, ReloadOutlined, UsergroupAddOutlined,
  SafetyOutlined, GlobalOutlined, BranchesOutlined, LockOutlined
} from '@ant-design/icons';
import sharepointAPI from '../../services/sharePointAPI';
import ManageAccessModal from './ManageAccessModal';
import FileCollaborationDrawer from './FileCollaborationDrawer';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const VALID_DEPARTMENTS = [
  'IT',
  'Technical',
  'Business Development & Supply Chain',
  'HR & Admin',
  'Finance',
  'Company',   // ← org-wide, visible to everyone
  'Other'
];


// Department list kept in sync with DEPARTMENT_STRUCTURE
const DEPARTMENTS = [
  'IT',
  'Technical',
  'Business Development & Supply Chain',
  'HR & Admin',
  'Finance',
  'Company',
  'Other'
];


const SharePointPortal = () => {
  const navigate  = useNavigate();
  const { user }  = useSelector((state) => state.auth);

  const [folders,      setFolders]      = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [files,        setFiles]        = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [viewMode,      setViewMode]      = useState('folders'); // 'folders' | 'shared'
  const [sharedFiles,   setSharedFiles]   = useState([]);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [breadcrumbs,  setBreadcrumbs]  = useState([{ label: 'Home', key: 'home' }]);
  const [searchQuery,  setSearchQuery]  = useState('');
  const [loading,      setLoading]      = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);

  const [uploadModalVisible,      setUploadModalVisible]      = useState(false);
  const [folderModalVisible,      setFolderModalVisible]      = useState(false);
  const [shareModalVisible,       setShareModalVisible]       = useState(false);
  const [manageAccessModalVisible, setManageAccessModalVisible] = useState(false);
  const [collaborationDrawerVisible, setCollaborationDrawerVisible] = useState(false);

  const [form]       = Form.useForm();
  const [uploadForm] = Form.useForm();
  const [shareForm]  = Form.useForm();

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFile,  setSelectedFile]  = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [drawerFile,    setDrawerFile]    = useState(null);

  const [stats, setStats] = useState({ totalFiles: 0, totalFolders: 0, totalStorage: 0, totalDownloads: 0 });
  const [userDepartmentInfo, setUserDepartmentInfo] = useState(null);

  useEffect(() => { fetchFolders(); fetchStats(); }, []);
  useEffect(() => {
    if (currentFolder) { fetchFiles(currentFolder); setSearchQuery(''); }
    else setFiles([]);
  }, [currentFolder]);

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const res = await sharepointAPI.getFolders();
      const data = res.data.data || [];
      setFolders(data);
      setUserDepartmentInfo({ department: res.data.userDepartment, role: res.data.userRole });

      if (data.length > 0 && !currentFolder) {
        const def = data.find(f => f.department === res.data.userDepartment) || data[0];
        setCurrentFolder(def._id);
        setBreadcrumbs([{ label: 'Home', key: 'home' }, { label: def.name, key: def._id }]);
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to load folders');
    } finally { setLoading(false); }
  };

  const fetchFiles = async (folderId, search = '') => {
    try {
      setLoading(true);
      const res = await sharepointAPI.getFiles(folderId, search ? { search } : {});
      setFiles(res.data.data || []);
      await fetchStats();
    } catch (err) {
      if (err.response?.status === 403) message.error('You do not have access to this folder');
      else message.error('Failed to load files');
      setFiles([]);
    } finally { setLoading(false); }
  };

  // Files shared directly with the current user - discoverable here regardless of whether
  // they have independent folder-level access to wherever the file actually lives.
  const fetchSharedFiles = async () => {
    try {
      setSharedLoading(true);
      const res = await sharepointAPI.getSharedWithMe();
      setSharedFiles(res.data.data || []);
    } catch (err) {
      message.error('Failed to load files shared with you');
      setSharedFiles([]);
    } finally { setSharedLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await sharepointAPI.getUserStats();
      const d   = res.data.data;
      setStats({
        totalFiles:     d.uploads?.filesUploaded || 0,
        totalStorage:   d.uploads?.totalSize     || 0,
        totalDownloads: d.uploads?.totalDownloads || 0,
        totalFolders:   folders.length
      });
    } catch (_) {}
  };

  const handleNavigateFolder = useCallback((folder) => {
    setCurrentFolder(folder._id);
    setBreadcrumbs(buildBreadcrumbForFolder(folder));
  }, [folders]);

  // Walks parentFolder links within the already-fetched flat folder list to build
  // Home > Grandparent > Parent > This Folder without any extra API calls.
  const buildBreadcrumbForFolder = (folder) => {
    const chain = [];
    let node = folder;
    const visited = new Set();
    while (node && !visited.has(node._id)) {
      visited.add(node._id);
      chain.unshift({ label: node.name, key: node._id });
      const parentId = node.parentFolder?._id || node.parentFolder;
      node = parentId ? folders.find(f => f._id === parentId) : null;
    }
    return [{ label: 'Home', key: 'home' }, ...chain];
  };

  // const handleCreateFolder = async (values) => {
  //   try {
  //     setLoading(true);
  //     await sharepointAPI.createFolder({
  //       name:             values.folderName,
  //       description:      values.description,
  //       department:       user.role === 'admin' ? values.department : user.department,
  //       privacyLevel:     values.privacyLevel,
  //       allowedDepartments: []
  //     });
  //     message.success('Folder created');
  //     form.resetFields();
  //     setFolderModalVisible(false);
  //     await fetchFolders();
  //   } catch (err) {
  //     message.error(err.response?.data?.message || 'Failed to create folder');
  //   } finally { setLoading(false); }
  // };

  const handleCreateFolder = async (values) => {
    try {
      setLoading(true);
      await sharepointAPI.createFolder({
        name:         values.folderName,
        description:  values.description,
        // Only send department if admin — server ignores it for non-admins anyway
        ...(user.role === 'admin' && !currentFolder ? { department: values.department } : {}),
        privacyLevel: values.privacyLevel,
        // Creating while a folder is open makes this a subfolder of it; the server
        // inherits department from the parent and enforces privacy can't be looser.
        ...(currentFolder ? { parentFolderId: currentFolder } : {}),
      });
      message.success(currentFolder ? 'Subfolder created successfully' : 'Folder created successfully');
      form.resetFields();
      setFolderModalVisible(false);
      await fetchFolders();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to create folder');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFiles = async (values) => {
    if (!currentFolder) return message.warning('Select a folder first');
    if (!uploadedFiles.length) return message.warning('Select at least one file');

    try {
      setUploadLoading(true);
      const fd = new FormData();

      if (uploadedFiles.length === 1) {
        // Single-file endpoint expects exactly one file under the field name 'file'.
        fd.append('file', uploadedFiles[0].originFileObj || uploadedFiles[0]);
        if (values.description) fd.append('description', values.description);
        if (values.tags)        fd.append('tags', values.tags);
        await sharepointAPI.uploadFile(currentFolder, fd);
      } else {
        // Multiple files must go to the bulk endpoint, which expects them all under
        // the field name 'files' (plural) - the single-file endpoint rejects any
        // file beyond the first one sent under 'file'.
        uploadedFiles.forEach(f => fd.append('files', f.originFileObj || f));
        if (values.description) fd.append('description', values.description);
        if (values.tags)        fd.append('tags', values.tags);
        await sharepointAPI.bulkUploadFiles(currentFolder, fd);
      }

      message.success(`${uploadedFiles.length} file(s) uploaded`);
      setUploadedFiles([]);
      uploadForm.resetFields();
      setUploadModalVisible(false);
      await fetchFiles(currentFolder);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to upload');
    } finally { setUploadLoading(false); }
  };

  // ── Download: uses openFileDownload (works for both local + Cloudinary) ──────
  const handleDownloadFile = async (file) => {
    try {
      await sharepointAPI.openFileDownload(file._id, file.name);
      message.success(`Downloaded: ${file.name}`);
    } catch {
      message.error('Download failed');
    }
  };

  const handleDeleteFile = (fileId, fileName, hasCollaborators) => {
    Modal.confirm({
      title: 'Delete File',
      content: hasCollaborators
        ? `"${fileName}" has collaborators added to it. Remove them first (or ask an admin) before deleting.`
        : `Delete "${fileName}"?`,
      okText: 'Delete', okType: 'danger',
      okButtonProps: { disabled: hasCollaborators },
      async onOk() {
        if (hasCollaborators) return;
        try {
          await sharepointAPI.deleteFile(fileId, false);
          message.success('File deleted');
          await fetchFiles(currentFolder);
        } catch (error) {
          message.error(error.response?.data?.message || 'Failed to delete file');
        }
      }
    });
  };

  const handleShareFile = async (values) => {
    try {
      if (!selectedFile) return;
      await sharepointAPI.shareFile(selectedFile._id, { shareWith: values.shareWith, permission: values.permissions, type: values.type });
      message.success('File shared');
      setShareModalVisible(false);
      shareForm.resetFields();
      setSelectedFile(null);
    } catch (err) { message.error(err.response?.data?.message || 'Failed to share'); }
  };

  const handleGenerateShareLink = async (fileId) => {
    try {
      const res = await sharepointAPI.generateShareLink(fileId, 7 * 24 * 60 * 60);
      const link = res.data.data.shareLink;
      Modal.success({
        title: 'Share Link (expires in 7 days)',
        content: (
          <Input value={link} readOnly
            addonAfter={<CopyOutlined onClick={() => { navigator.clipboard.writeText(link); message.success('Copied!'); }} />}
          />
        )
      });
    } catch { message.error('Failed to generate link'); }
  };

  const handleSearch = useCallback((value) => {
    setSearchQuery(value);
    if (currentFolder) fetchFiles(currentFolder, value);
  }, [currentFolder]);

  const openDrawer = (file) => { setDrawerFile(file); setCollaborationDrawerVisible(true); };

  // ── Utils ──────────────────────────────────────────────────────────────────
  const fmtSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024, s = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + s[i];
  };

  const getFileIcon = (mime, name) => {
    const t = mime || name?.split('.').pop()?.toLowerCase() || '';
    if (t.includes('pdf'))   return <FilePdfOutlined   style={{ color: '#ff4d4f' }} />;
    if (t.includes('image') || ['jpg','jpeg','png','gif'].includes(t))
      return <FileImageOutlined style={{ color: '#52c41a' }} />;
    if (t.includes('word') || ['doc','docx'].includes(t))
      return <FileWordOutlined  style={{ color: '#1890ff' }} />;
    if (t.includes('excel') || ['xls','xlsx'].includes(t))
      return <FileExcelOutlined style={{ color: '#52c41a' }} />;
    if (t.includes('text') || ['txt','csv'].includes(t))
      return <FileTextOutlined  style={{ color: '#faad14' }} />;
    return <FileOutlined />;
  };

  const getPrivacyIcon = (level) => {
    if (level === 'confidential') return <SafetyOutlined style={{ color: '#ff4d4f' }} />;
    if (level === 'public')       return <GlobalOutlined  style={{ color: '#52c41a' }} />;
    return <TeamOutlined style={{ color: '#1890ff' }} />;
  };

  // ── Table columns ──────────────────────────────────────────────────────────
  const fileColumns = [
    {
      title: 'File Name', dataIndex: 'name', key: 'name',
      render: (name, record) => {
        const co = record.checkoutStatus;
        return (
          <Space>
            {getFileIcon(record.mimetype, record.name)}
            <div>
              <Text ellipsis style={{ maxWidth: 180 }}>{name}</Text>
              <div style={{ marginTop: 2 }}>
                {co?.isLocked && (
                  <Tag icon={<LockOutlined />} color={co.checkedOutByMe ? 'purple' : 'red'} style={{ fontSize: 10 }}>
                    {co.checkedOutByMe ? 'Checked out by you' : `Locked by ${co.checkedOutBy?.fullName || 'someone'}`}
                  </Tag>
                )}
                {record.collaborators?.length > 0 && (
                  <Tag icon={<TeamOutlined />} color="geekblue" style={{ fontSize: 10 }}>
                    {record.collaborators.length} collab{record.collaborators.length > 1 ? 's' : ''}
                  </Tag>
                )}
              </div>
            </div>
          </Space>
        );
      }
    },
    { title: 'Size',        dataIndex: 'size',                    key: 'size',       width: 90,  render: (s) => fmtSize(s) },
    { title: 'Uploaded By', dataIndex: ['uploadedBy', 'fullName'], key: 'uploadedBy', width: 120 },
    { title: 'Date',        dataIndex: 'uploadedAt',              key: 'uploadedAt', width: 110,
      render: (d) => d ? new Date(d).toLocaleDateString() : '—' },
    { title: 'Downloads',   dataIndex: 'downloads',               key: 'downloads',  width: 80,
      render: (c) => <Badge count={c} style={{ backgroundColor: '#1890ff' }} /> },
    {
      title: 'Actions', key: 'actions', width: 230,
      render: (_, record) => {
        const p = record.userPermissions || {};
        return (
          <Space size="small">
            {p.canDownload !== false && (
              <Tooltip title="Download">
                <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => handleDownloadFile(record)} />
              </Tooltip>
            )}
            <Tooltip title="Versions · Comments · Checkout · Audit Trail">
              <Button type="link" size="small" icon={<BranchesOutlined />}
                onClick={() => openDrawer(record)} style={{ color: '#722ed1' }} />
            </Tooltip>
            {p.canShare !== false && (
              <Tooltip title="Share">
                <Button type="link" size="small" icon={<ShareAltOutlined />}
                  onClick={() => { setSelectedFile(record); setShareModalVisible(true); }} />
              </Tooltip>
            )}
            {p.canShare !== false && (
              <Tooltip title="Get Share Link">
                <Button type="link" size="small" icon={<CopyOutlined />}
                  onClick={() => handleGenerateShareLink(record._id)} />
              </Tooltip>
            )}
            {p.canDelete === true && (
              <Tooltip title="Delete">
                <Button type="link" size="small" danger icon={<DeleteOutlined />}
                  onClick={() => handleDeleteFile(record._id, record.name, p.hasCollaborators)} />
              </Tooltip>
            )}
          </Space>
        );
      }
    }
  ];

  // ── Sidebar tree ───────────────────────────────────────────────────────────
  // Recursively builds real nested tree nodes from the flat `folders` list using
  // parentFolder links, instead of a flat per-department leaf list that couldn't
  // represent subfolders at all.
  const buildFolderNode = (folder) => {
    const children = folders
      .filter(f => (f.parentFolder?._id || f.parentFolder) === folder._id)
      .map(buildFolderNode);

    return {
      key: folder._id,
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 0' }}
          onClick={() => handleNavigateFolder(folder)}>
          <FolderOutlined style={{ color: '#faad14' }} />
          <Text>{folder.name}</Text>
          {getPrivacyIcon(folder.privacyLevel)}
          {folder.fileCount > 0 && (
            <Tag style={{ marginLeft: 4, fontSize: 10, lineHeight: '16px', padding: '0 6px' }} color="blue">
              {folder.fileCount} file{folder.fileCount === 1 ? '' : 's'}
            </Tag>
          )}
        </div>
      ),
      selectable: true,
      isLeaf: children.length === 0,
      children: children.length > 0 ? children : undefined
    };
  };

  const rootFolders = folders.filter(f => !f.parentFolder);
  const byDept = rootFolders.reduce((acc, f) => {
    const d = f.department || 'Other';
    if (!acc[d]) acc[d] = [];
    acc[d].push(f);
    return acc;
  }, {});

  const folderTreeData = Object.entries(byDept).map(([dept, dfs]) => ({
    key: `dept-${dept}`,
    title: <Text strong style={{ fontSize: 13 }}><TeamOutlined /> {dept}</Text>,
    selectable: false,
    children: dfs.map(buildFolderNode)
  }));

  // All node keys, used to keep the tree fully expanded (see expandedKeys below).
  const allTreeKeys = [
    ...Object.keys(byDept).map(dept => `dept-${dept}`),
    ...folders.map(f => f._id)
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setExpandedKeys(allTreeKeys); }, [folders]);

  const currentFolderData = folders.find(f => f._id === currentFolder);
  const filteredFiles = files.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()));

  if (loading && !currentFolder) {
    return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /><div style={{ marginTop: 16 }}>Loading…</div></div>;
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Card style={{ marginBottom: 24, background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)' }}>
        <Row align="middle" justify="space-between">
          <Col xs={24} sm={12}>
            <Title level={2} style={{ color: 'white', margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
              <ShareAltOutlined /> File Sharing Portal
            </Title>
            {userDepartmentInfo && (
              <Text style={{ color: 'rgba(255,255,255,.85)', fontSize: 14, display: 'block', marginTop: 4 }}>
                Department: {userDepartmentInfo.department} | Role: {userDepartmentInfo.role}
              </Text>
            )}
          </Col>
          <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
            <Space>
              <Button type="primary" icon={<FolderAddOutlined />} onClick={() => setFolderModalVisible(true)}
                style={{ backgroundColor: 'rgba(255,255,255,.3)', borderColor: 'white', color: 'white' }}>
                {currentFolder ? 'New Subfolder' : 'New Folder'}
              </Button>
              <Button type="primary" icon={<UploadOutlined />}
                onClick={() => { if (!currentFolder) return message.warning('Select a folder first'); setUploadModalVisible(true); }}
                disabled={!currentFolder}
                style={{ background: 'white', color: '#667eea', border: 'none' }}>
                Upload Files
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total Files',     value: stats.totalFiles,                  icon: <FileOutlined />,     color: '#1890ff' },
          { title: 'Total Folders',   value: folders.length,                    icon: <FolderOutlined />,   color: '#faad14' },
          { title: 'Total Downloads', value: stats.totalDownloads,              icon: <DownloadOutlined />, color: '#52c41a' },
          { title: 'Storage Used',    value: fmtSize(stats.totalStorage),       icon: null,                 color: '#722ed1' }
        ].map(s => (
          <Col xs={24} sm={12} md={6} key={s.title}>
            <Card><Statistic title={s.title} value={s.value} prefix={s.icon} valueStyle={{ color: s.color }} /></Card>
          </Col>
        ))}
      </Row>

      {/* Main layout */}
      <Row gutter={24}>
        {/* Sidebar */}
        <Col xs={24} md={6}>
          <Card title="Folders by Department" size="small" style={{ marginBottom: 16 }}>
            <Button type="text" block icon={<HomeOutlined />}
              onClick={() => { setViewMode('folders'); setCurrentFolder(null); setBreadcrumbs([{ label: 'Home', key: 'home' }]); }}
              style={{ textAlign: 'left', marginBottom: 8, justifyContent: 'flex-start' }}>
              All Folders
            </Button>
            <Button type="text" block icon={<ShareAltOutlined />}
              onClick={() => { setViewMode('shared'); fetchSharedFiles(); }}
              style={{ textAlign: 'left', marginBottom: 8, justifyContent: 'flex-start' }}>
              Shared With Me
            </Button>
            <Divider style={{ margin: '12px 0' }} />
            <Tree treeData={folderTreeData} selectedKeys={currentFolder ? [currentFolder] : []}
              expandedKeys={expandedKeys}
              onExpand={(keys) => setExpandedKeys(keys)}
              onSelect={(keys) => {
                if (keys.length && !keys[0].toString().startsWith('dept-')) {
                  const f = folders.find(f => f._id === keys[0]);
                  if (f) handleNavigateFolder(f);
                }
              }} />
          </Card>
          <Card size="small">
            <Button block icon={<ReloadOutlined />} loading={loading}
              onClick={() => { fetchFolders(); if (currentFolder) fetchFiles(currentFolder); }}>
              Refresh
            </Button>
          </Card>
        </Col>

        {/* Content */}
        <Col xs={24} md={18}>
          {viewMode === 'shared' ? (
            <Card title="Files Shared With Me">
              <Table
                loading={sharedLoading}
                dataSource={sharedFiles}
                rowKey="_id"
                locale={{ emptyText: 'No files have been shared with you yet' }}
                columns={[
                  { title: 'File Name', dataIndex: 'name', key: 'name' },
                  { title: 'Folder', key: 'folder', render: (_, r) => r.folderId?.name || '—' },
                  { title: 'Shared By', key: 'sharedBy', render: (_, r) => r.uploadedBy?.fullName || '—' },
                  {
                    title: 'Permission', dataIndex: 'sharedPermission', key: 'sharedPermission',
                    render: (p) => <Tag color="blue">{p}</Tag>
                  },
                  {
                    title: 'Shared', dataIndex: 'sharedAt', key: 'sharedAt',
                    render: (d) => d ? new Date(d).toLocaleDateString() : '—'
                  },
                  {
                    title: 'Actions', key: 'actions',
                    render: (_, record) => (
                      <Space>
                        <Button type="link" size="small" icon={<DownloadOutlined />}
                          onClick={() => handleDownloadFile(record)} />
                      </Space>
                    )
                  }
                ]}
              />
            </Card>
          ) : (
          <>
          <Breadcrumb style={{ marginBottom: 16 }}>
            {breadcrumbs.map(bc => (
              <Breadcrumb.Item key={bc.key}>
                <Button type="link" size="small"
                  onClick={() => { if (bc.key === 'home') { setCurrentFolder(null); setBreadcrumbs([{ label: 'Home', key: 'home' }]); } }}>
                  {bc.label}
                </Button>
              </Breadcrumb.Item>
            ))}
          </Breadcrumb>

          {currentFolderData && (
            <Card style={{ marginBottom: 16 }}>
              <Row gutter={16} align="middle">
                <Col xs={24} sm={16}>
                  <Space direction="vertical" size={4}>
                    <Title level={4} style={{ margin: 0 }}>{currentFolderData.name}</Title>
                    <Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>{currentFolderData.description}</Paragraph>
                    <Space wrap>
                      <Tag>{currentFolderData.department}</Tag>
                      <Tag color={currentFolderData.privacyLevel === 'confidential' ? 'red' : currentFolderData.privacyLevel === 'public' ? 'green' : 'blue'}>
                        {currentFolderData.privacyLevel?.toUpperCase()}
                      </Tag>
                      {currentFolderData.userAccess?.permission && (
                        <Tag color="purple">Your Access: {currentFolderData.userAccess.permission.toUpperCase()}</Tag>
                      )}
                    </Space>
                  </Space>
                </Col>
                <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
                  {currentFolderData.userAccess?.canManage && (
                    <Button type="primary" icon={<UsergroupAddOutlined />}
                      onClick={() => { setSelectedFolder(currentFolderData); setManageAccessModalVisible(true); }}>
                      Manage Access
                    </Button>
                  )}
                </Col>
              </Row>
            </Card>
          )}

          {currentFolder && (
            <Input.Search placeholder="Search files…" prefix={<SearchOutlined />}
              value={searchQuery} onChange={(e) => handleSearch(e.target.value)}
              allowClear size="large" style={{ marginBottom: 16 }} />
          )}

          {currentFolder ? (
            <Card title={
              <Space>
                <span>Files ({filteredFiles.length})</span>
                <Tooltip title="Click the branch icon on any file to check out, view versions, leave comments, and see the full audit trail.">
                  <Tag icon={<BranchesOutlined />} color="purple">Collaboration</Tag>
                </Tooltip>
              </Space>
            }>
              {loading
                ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                : filteredFiles.length > 0
                  ? <Table columns={fileColumns} dataSource={filteredFiles.map(f => ({ ...f, key: f._id }))}
                      pagination={{ pageSize: 10, size: 'small' }} scroll={{ x: 'max-content' }} size="small" />
                  : <Empty description={searchQuery ? 'No files match your search' : 'No files in this folder'}
                      style={{ margin: '40px 0' }} />
              }
            </Card>
          ) : (
            <Card>
              <Empty description="Select a folder to view its contents"
                image={<FolderOutlined style={{ fontSize: 64, color: '#faad14' }} />}
                style={{ margin: '40px 0' }}>
                <Text type="secondary">Browse folders by department from the sidebar</Text>
              </Empty>
            </Card>
          )}
          </>
          )}
        </Col>
      </Row>

      {/* Create Folder Modal */}
      <Modal title={currentFolder ? `New Subfolder in "${currentFolderData?.name}"` : 'Create New Folder'} open={folderModalVisible}
        onOk={() => form.submit()}
        onCancel={() => { setFolderModalVisible(false); form.resetFields(); }}
        confirmLoading={loading}>
        <Form form={form} layout="vertical" onFinish={handleCreateFolder}>
          <Form.Item name="folderName" label="Folder Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Q4 Reports" />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <TextArea rows={3} placeholder="Describe the folder contents…" />
          </Form.Item>
          {currentFolder && (
            <Form.Item label="Department">
              <Input value={currentFolderData?.department} disabled style={{ color: '#595959' }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Subfolders inherit their parent folder's department.
              </Text>
            </Form.Item>
          )}
          {!currentFolder && user.role === 'admin' && (
            <Form.Item
              name="department"
              label="Department"
              rules={[{ required: true, message: 'Please select a department' }]}
              extra="'Company' folders are visible to all employees by default."
            >
              <Select placeholder="Select department">
                {VALID_DEPARTMENTS.map(d => (
                  <Option key={d} value={d}>{d}</Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {!currentFolder && user.role !== 'admin' && (
            <Form.Item label="Department">
              <Input
                value={user.department}
                disabled
                style={{ color: '#595959' }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Folders are created in your own department. Admins can create cross-department folders.
              </Text>
            </Form.Item>
          )}
          <Form.Item name="privacyLevel" label="Privacy Level" rules={[{ required: true }]} initialValue="department">
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="public"><Space><GlobalOutlined style={{ color: '#52c41a' }} /><div><Text strong>Public</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>Everyone in the org can access</Text></div></Space></Radio>
                <Radio value="department"><Space><TeamOutlined style={{ color: '#1890ff' }} /><div><Text strong>Department</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>Only department members</Text></div></Space></Radio>
                <Radio value="confidential"><Space><SafetyOutlined style={{ color: '#ff4d4f' }} /><div><Text strong>Confidential</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>Invitation only, invisible to others</Text></div></Space></Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Files Modal */}
      <Modal title="Upload Files" open={uploadModalVisible}
        onOk={() => uploadForm.submit()}
        onCancel={() => { setUploadModalVisible(false); uploadForm.resetFields(); setUploadedFiles([]); }}
        confirmLoading={uploadLoading} width={600}>
        <Form form={uploadForm} layout="vertical" onFinish={handleUploadFiles}>
          <Form.Item label="Select Files" extra="Max 100 MB per file">
            <Upload beforeUpload={() => false} multiple
              onChange={(info) => setUploadedFiles(info.fileList)}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.gif">
              <Button icon={<UploadOutlined />}>Click or Drag Files</Button>
            </Upload>
            {uploadedFiles.length > 0 && (
              <List size="small" style={{ marginTop: 12 }} dataSource={uploadedFiles}
                renderItem={(f, i) => (
                  <List.Item key={i}>
                    <List.Item.Meta avatar={getFileIcon(f.type, f.name)} title={f.name} description={fmtSize(f.size)} />
                  </List.Item>
                )} />
            )}
          </Form.Item>
          <Form.Item name="description" label="Description (optional)"><TextArea rows={2} /></Form.Item>
          <Form.Item name="tags" label="Tags (optional)"><Input placeholder="budget, 2024, important" /></Form.Item>
        </Form>
      </Modal>

      {/* Share File Modal */}
      <Modal title={`Share: ${selectedFile?.name || ''}`} open={shareModalVisible}
        onOk={() => shareForm.submit()}
        onCancel={() => { setShareModalVisible(false); shareForm.resetFields(); setSelectedFile(null); }}>
        <Form form={shareForm} layout="vertical" onFinish={handleShareFile}>
          <Form.Item name="type" label="Share Type" rules={[{ required: true }]}>
            <Select placeholder="Select share type">
              <Option value="user">Specific User</Option>
              <Option value="department">Department</Option>
            </Select>
          </Form.Item>
          <Form.Item name="shareWith" label="Share With" rules={[{ required: true }]}>
            <Input placeholder="User email or department name" />
          </Form.Item>
          <Form.Item name="permissions" label="Permissions" rules={[{ required: true }]}>
            <Select>
              <Option value="view">View Only</Option>
              <Option value="download">Download</Option>
              <Option value="edit">Edit</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Manage Folder Access */}
      {selectedFolder && (
        <ManageAccessModal visible={manageAccessModalVisible}
          onClose={() => { setManageAccessModalVisible(false); setSelectedFolder(null); }}
          folder={selectedFolder}
          onSuccess={() => { fetchFolders(); if (currentFolder) fetchFiles(currentFolder); }} />
      )}

      {/* Collaboration Drawer */}
      {drawerFile && (
        <FileCollaborationDrawer visible={collaborationDrawerVisible}
          onClose={() => { setCollaborationDrawerVisible(false); setDrawerFile(null); }}
          file={drawerFile}
          onVersionUploaded={() => fetchFiles(currentFolder)} />
      )}
    </div>
  );
};

export default SharePointPortal;


